export type NavTabPath = [navTabNumber: number];
export type PagePath = [navTabNumber: number, pageNumber: number];
export type SectionPath = [navTabNumber: number, pageNumber: number, sectionCode: string];
export type QuestionPath = [navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string];

export type NavTabInfo = { navTabPath: NavTabPath };
export type PageInfo = { pagePath: PagePath, isRepeatingPage: boolean };
export type SectionInfo = { sectionPath: SectionPath, isRepeatingPage: boolean, isRepeatingSection: boolean };
export type QuestionInfo = { questionPath: QuestionPath, isRepeatingPage: boolean, isRepeatingSection: boolean };

type ConfigKey = string | number | (string | number)[];

// This type could be made a bit narrower than just "any".
// However the type definition would end up being so complex that is probably not worth the effort.
export type FormlyFieldConfig = any;

export class ConfigDataParser {

  static getNavTabConfigs(formConfig: FormlyFieldConfig[]): FormlyFieldConfig[] {
    const navTabConfigs = formConfig?.[0]?.fieldGroup?.[0]?.fieldGroup;
    return navTabConfigs;
  }

  static getNavTabConfig(formConfig: FormlyFieldConfig[], navTabPath: NavTabPath): FormlyFieldConfig {
    const [navTabNumber] = navTabPath;
    const navTabConfigKey = `${this.NavTabPrefix}${navTabNumber}`;
    const navTabConfig = this.getNavTabConfigs(formConfig)
      ?.filter(navTabConfig => navTabConfig.key === navTabConfigKey)?.[0];
    return navTabConfig;
  }

  static getPageConfigs(formConfig: FormlyFieldConfig[], navTabPath: NavTabPath): FormlyFieldConfig[] {
    const pageConfigs = this.getNavTabConfig(formConfig, navTabPath)?.fieldGroup;
    return pageConfigs;
  }

  static getPageConfig(formConfig: FormlyFieldConfig[], pagePath: PagePath): FormlyFieldConfig {
    const [navTabNumber, pageNumber] = pagePath;
    const navTabPath: NavTabPath = [navTabNumber];
    const pageConfigKey = `${this.PagePrefix}${pageNumber}`;
    const pageConfig = this.getPageConfigs(formConfig, navTabPath)
      ?.filter(pageConfig => pageConfig.key === pageConfigKey)?.[0];
    return pageConfig;
  }

  static getSectionConfigs(formConfig: FormlyFieldConfig[], pagePath: PagePath): FormlyFieldConfig[] {
    const pageConfig = this.getPageConfig(formConfig, pagePath);
    const sectionConfigs = (pageConfig?.fieldArray != null)
      ? (pageConfig?.fieldArray as FormlyFieldConfig).fieldGroup
      : pageConfig?.fieldGroup;
    return sectionConfigs;
  }

  static getSectionConfig(formConfig: FormlyFieldConfig[], sectionPath: SectionPath): FormlyFieldConfig {
    const [navTabNumber, pageNumber, sectionCode] = sectionPath;
    const pagePath: PagePath = [navTabNumber, pageNumber];
    const sectionConfigKey = sectionCode;
    const sectionConfig = this.getSectionConfigs(formConfig, pagePath)
      ?.filter(sectionConfig => sectionConfig.key === sectionConfigKey)?.[0];
    return sectionConfig;
  }

  static getQuestionConfigs(formConfig: FormlyFieldConfig[], sectionPath: SectionPath): FormlyFieldConfig[] {
    const sectionConfig = this.getSectionConfig(formConfig, sectionPath);
    const questionConfigs = (sectionConfig?.fieldArray != null)
      ? (sectionConfig?.fieldArray as FormlyFieldConfig).fieldGroup
      : sectionConfig?.fieldGroup;
    return questionConfigs;
  }

  static getQuestionConfig(formConfig: FormlyFieldConfig[], questionPath: QuestionPath): FormlyFieldConfig {
    const [navTabNumber, pageNumber, sectionCode, questionCode] = questionPath;
    const sectionPath: SectionPath = [navTabNumber, pageNumber, sectionCode];
    const questionConfigKey = questionCode;
    const questionConfig = this.getQuestionConfigs(formConfig, sectionPath)
      ?.filter(questionConfig => questionConfig.key === questionConfigKey)?.[0];
    return questionConfig;
  }

  static getNavTabPaths(formConfig: FormlyFieldConfig[]): NavTabPath[] {
    return this.getNavTabConfigs(formConfig)
      ?.map(navTabConfig =>
        [this.getNavTabNumber(navTabConfig.key)]);
  }

  static getPagePaths(formConfig: FormlyFieldConfig[]): PagePath[] {
    return this.getNavTabPaths(formConfig)
      ?.map<PagePath[]>(navTabPath => this.getPageConfigs(formConfig, navTabPath)
        ?.map<PagePath>(pageConfig => [...navTabPath, this.getPageNumber(pageConfig.key)]))
      ?.flat();
  }

  static getSectionPaths(formConfig: FormlyFieldConfig[]): SectionPath[] {
    return this.getPagePaths(formConfig)
      ?.map<SectionPath[]>(pagePath => this.getSectionConfigs(formConfig, pagePath)
        ?.map<SectionPath>(sectionConfig => [...pagePath, this.getSectionCode(sectionConfig.key)]))
      ?.flat();
  }

  static getQuestionPaths(formConfig: FormlyFieldConfig[]): QuestionPath[] {
    return this.getSectionPaths(formConfig)
      ?.map<QuestionPath[]>(sectionPath => this.getQuestionConfigs(formConfig, sectionPath)
        ?.map<QuestionPath>(questionConfig => [...sectionPath, this.getQuestionCode(questionConfig.key)]))
      ?.flat();
  }

  private static readonly NavTabPrefix = 'tab';
  private static readonly PagePrefix = 'page';

  private static readonly NavTabPrefixLength = this.NavTabPrefix.length;
  private static readonly PagePrefixLength = this.PagePrefix.length;

  private static getNavTabNumber = (navTabKey: ConfigKey): number => parseInt((navTabKey as string)?.substring(this.NavTabPrefixLength));
  private static getPageNumber = (pageKey: ConfigKey): number => parseInt((pageKey as string)?.substring(this.PagePrefixLength));
  private static getSectionCode = (sectionKey: ConfigKey): string => sectionKey as string;
  private static getQuestionCode = (questionKey: ConfigKey): string => questionKey as string;
}
